import React from 'react'
import { generateUUID } from '@/utils'
import { startSSEStrategy } from './check-app-version'

/** 测试/生产站点（version.json 地址分别为 {origin}/version.json） */
const AIGC_ORIGIN = {
  test: 'https://test-aigc.aiadfly.com',
  prod: 'https://aigc.aiadfly.com'
} as const

interface AppCheckVersionOptions {
  key?: string
  env: 'test' | 'prod'
  runtimeEnv?: 'dev' | 'prod'
  project?: 'aigc-web'
}

interface WebSocketMessage {
  type: 'json' | 'string' | 'connect' | 'heartbeat'
  data: unknown
  time: string
}

interface ConnectMessage {
  type: 'connect'
  key: string
  timestamp: number
}

const WS_CONFIG = {
  MAX_RETRY_COUNT: 20,
  RETRY_DELAY: 30 * 1000,
  POLLING_INTERVAL: 3 * 1000
}

/** 与 client 共用 fe 推送服务，部署后由 deploy-cli 调用 sseDomain 通知 */
const DOMAIN_MAP: Record<
  string,
  Record<string, { test: string; mock: string; dev: string; prod: string }>
> = {
  'aigc-web': {
    'zh-CN': {
      test: 'wss://test-fe.aiadfly.com',
      mock: 'wss://test-fe.aiadfly.com',
      dev: 'wss://test-fe.aiadfly.com',
      prod: 'wss://fe.aiadfly.com'
    }
  }
}

const isWebSocketSupported = (): boolean => {
  return typeof WebSocket !== 'undefined'
}

export const useAppCheckVersionUpdate = (opt: AppCheckVersionOptions) => {
  const { env, key = '', project = 'aigc-web' } = opt

  const [openStatus, setOpenStatus] = React.useState<boolean>(false)
  const keyRef = React.useRef<string>('')
  const wsRef = React.useRef<WebSocket | null>(null)
  const pollingTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const retryCountRef = React.useRef<number>(0)

  const domain = React.useMemo<string | undefined>(() => {
    const domainConfig = DOMAIN_MAP?.[project]
    return domainConfig?.['zh-CN']?.[env]
  }, [env, project])

  const handleVersionUpdate = (versionInfo: { version: string; name: string }) => {
    startSSEStrategy({
      viteModeEnv: opt.env,
      serverVersionInfo: versionInfo
    })
  }

  const handleWebSocketMessage = (message: WebSocketMessage): void => {
    if (message.type === 'json') {
      const transferData = (message?.data as { transferData?: { name?: string; version?: string } })
        ?.transferData
      const name = transferData?.name
      const version = transferData?.version
      if (name && version) {
        pollVersionCheck()
      }
    }
    if (message.type === 'heartbeat') {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'heartbeat', timestamp: Date.now() }))
      }
    }
  }

  const pollVersionCheck = async () => {
    if (opt.runtimeEnv === 'dev') {
      return
    }
    let origin = location.origin
    if (origin.includes('localhost')) {
      origin = opt.env === 'prod' ? AIGC_ORIGIN.prod : AIGC_ORIGIN.test
    }
    try {
      const response = await fetch(`${origin}/version.json?t=${Date.now()}`)
      const data = await response.json()
      if (data?.version && data?.name) {
        handleVersionUpdate({ version: data.version, name: data.name })
      }
    } catch (error) {
      console.error('轮询版本检查失败:', error)
    }
  }

  const pollingCallback = async () => {
    pollVersionCheck()
  }

  const startPolling = (reason?: string) => {
    if (pollingTimerRef.current) return

    console.log('启动轮询兜底机制', reason)
    pollVersionCheck()
    pollingTimerRef.current = setInterval(pollingCallback, WS_CONFIG.POLLING_INTERVAL)
  }

  const stopPolling = () => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current)
      pollingTimerRef.current = null
      console.log('停止轮询兜底机制')
    }
  }

  const handleWebSocketOpen = (ws: WebSocket) => {
    console.log('WebSocket 连接成功')
    setOpenStatus(true)
    retryCountRef.current = 0
    stopPolling()
    const connectMessage: ConnectMessage = {
      type: 'connect',
      key: keyRef.current,
      timestamp: Date.now()
    }
    ws.send(JSON.stringify(connectMessage))
  }

  const retryWebSocket = (url: string) => {
    retryCountRef.current++
    if (retryCountRef.current <= WS_CONFIG.MAX_RETRY_COUNT) {
      setTimeout(() => {
        console.log(
          `尝试重新连接 WebSocket... (${retryCountRef.current}/${WS_CONFIG.MAX_RETRY_COUNT})`
        )
        createWebSocketConnection(url)
      }, WS_CONFIG.RETRY_DELAY)
    } else {
      console.log('WebSocket 重试次数已达上限')
    }
  }

  const handleWebSocketClose = (event: CloseEvent, url: string) => {
    setOpenStatus(false)
    console.log('WebSocket 连接关闭:', event.code, event.reason)
    startPolling('WebSocketClose')
    retryWebSocket(url)
  }

  const handleWebSocketError = (error: Event, url: string) => {
    setOpenStatus(false)
    console.error('WebSocket 错误:', error)
    startPolling('WebSocketError')
    retryWebSocket(url)
  }

  const createWebSocketConnection = (url: string): void => {
    try {
      const ws = new WebSocket(url)
      wsRef.current = ws
      ws.onopen = () => handleWebSocketOpen(ws)
      ws.onmessage = (event: MessageEvent) => {
        try {
          if (event.data) {
            const data: WebSocketMessage = JSON.parse(event.data)
            handleWebSocketMessage(data)
          }
        } catch (err) {
          console.error('解析 WebSocket 消息错误:', err)
        }
      }
      ws.onclose = (event: CloseEvent) => handleWebSocketClose(event, url)
      ws.onerror = (event: Event) => handleWebSocketError(event, url)
    } catch (error) {
      console.error('创建 WebSocket 连接失败:', error)
      setOpenStatus(false)
    }
  }

  const closeWebSocket = (): void => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close(1000, '主动关闭连接')
    }
    startPolling('closeWebSocket')
  }

  const cleanup = () => {
    closeWebSocket()
    stopPolling()
  }

  React.useEffect(() => {
    const interval = setInterval(() => {
      pollVersionCheck()
    }, 1000 * 30)

    return () => {
      clearInterval(interval)
    }
  }, [])

  React.useEffect(() => {
    if (opt.runtimeEnv === 'dev') {
      return
    }
    if (!isWebSocketSupported()) {
      console.warn('浏览器不支持 WebSocket，启动轮询兜底')
      startPolling('BrowserNotSupported')
      return
    }

    if (!domain) {
      console.warn('域名配置错误')
      return
    }

    setOpenStatus(false)
    const connectionKey = key || generateUUID()
    keyRef.current = connectionKey
    const wsUrl = `${domain}/fe/ws?key=${connectionKey}`
    createWebSocketConnection(wsUrl)

    const handleOnline = () => {
      console.log('网络恢复，尝试重连 WebSocket')
      if (!openStatus) {
        retryCountRef.current = 0
        createWebSocketConnection(wsUrl)
      }
    }

    const handleOffline = () => {
      console.log('网络断开，停止轮询')
      stopPolling()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      cleanup()
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [domain])
}
